import React, { Suspense, useEffect, useMemo, useState } from 'react';
import Breadcrumb from '../../../components/ui/materialui/breadcrumb';
import { Box, Paper, Button } from '@mui/material';
import InputSelectSearch from '../../../components/inputs/yup/materialui/inputSelectSearch';
import { useEjecutador } from '../hooks/useEjecutador';
import { FieldError, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from "yup";
import InputText from '../../../components/inputs/yup/materialui/inputText';
import InputFecha from '../../../components/inputs/yup/materialui/inputFecha';
import InputNumber from '../../../components/inputs/yup/materialui/inputNumber';
import InputRut from '../../../components/inputs/yup/materialui/inputRut';
import Tabla from '../../../components/ui/materialui/tabla';
import TableHeader from '../../../components/ui/materialui/tableHeader';
import MButton from '../../../components/inputs/yup/materialui/mButton';
import { SummarizeTwoTone } from '@mui/icons-material';
import { exportExcel } from '../../../utils/exportExcel';
import CustomAccordion from '../../../components/ui/materialui/customAccordion';
import { PermissionGuard } from '../../../components/auth/ProtectedRoute';
import { TipoFuncionalidad } from '../../../utils/enums';

const CARTERA_TOTAL_QUERY_ID = '146';

// Filtro del formulario principal
export interface FiltroQuery {
    idConsulta: string;
    nombreConsulta?: any[];
}

const schemaMandatario = Yup.object<FiltroQuery>().shape({
    idConsulta: Yup.string()
        .trim()
        .required('Campo requerido'),

    nombreConsulta: Yup.array()
        .default([])           // <-- si viene undefined, usa []
        .of(
            Yup.mixed().test('is-not-empty', 'Campo requerido', value => {
                return value !== undefined && value !== null && value !== '';
            })
        ),
        // <-- quitamos .required() del array
});

function EjecutadorQuery() {
    const { setQuerysState, setQueryParamsState, setQueryTransversalState, setQueryCarteraTotalState, setQueryTransversal, queryParams, querys, queryTransversal } = useEjecutador();
    const [nameArchive, setNameArchive] = useState<string>("Listado-Reporte");

    const {
        formState: { errors },
        handleSubmit,
        control,
        reset,
        unregister,
        clearErrors,
    } = useForm<FiltroQuery>({
        resolver: yupResolver(schemaMandatario),
        shouldUnregister: true,
        defaultValues: { idConsulta: "", nombreConsulta: [] },
    });


    useEffect(() => {
        setQuerysState();
        //eslint-disable-next-line
    }, []);


    const breadcrumbItems = [
        { label: 'Inicio', url: '/' },
        { label: 'Ejecutador querys', active: true },
    ];

    const onSubmit = async (data: FiltroQuery) => {
        const parametros: { [key: number]: any } = {};

        queryParams.forEach((param, index) => {
            const orden = param.orden;
            const valor = data.nombreConsulta?.[index];
            parametros[orden] = valor;
        });

        const selectedOption = querys.find(q => q.value === data.idConsulta);
        setNameArchive(selectedOption?.label ?? "Listado-Reporte");

        if (data.idConsulta === CARTERA_TOTAL_QUERY_ID) {
            setQueryTransversal([]);
            await setQueryCarteraTotalState();
            return;
        }

        setQueryTransversalState(data.idConsulta, parametros);
    };

    const columnasDinamicas = useMemo(() => {
        if (!Array.isArray(queryTransversal) || queryTransversal.length === 0 || typeof queryTransversal[0] !== "object") {
            return [];
        }

        return Object.keys(queryTransversal[0])
            .filter((key) => {
                const value = queryTransversal[0][key];
                return ['string', 'number', 'boolean'].includes(typeof value) || value == null;
            })
            .map((key) => ({
                name: key,
                selector: (row: any) => {
                    const val = row[key];
                    if (typeof val === 'boolean') return val ? "Sí" : "No";
                    // Validar si val es una fecha en formato 'YYYY-MM-DDTHH:mm:ss' o similar
                    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(val)) {
                        const date = new Date(val);
                        if (!isNaN(date.getTime())) {
                            return date.toLocaleDateString('es-CL');
                        }
                    }
                    return val != null ? val : "";
                },
                sortField: key,
                sortable: true,
                export: true,
            }));

    }, [queryTransversal]);

    return (
        <Suspense>
            <Breadcrumb items={breadcrumbItems} />
            <Box sx={{ mt: 2 }}>
                <Paper elevation={3} sx={{ p: 3 }}>
                    <CustomAccordion

                        title='Consultas'
                        expandedDefault
                    >
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <InputSelectSearch
                                label="Nombre consulta"
                                name="idConsulta"
                                options={querys}
                                control={control}
                                error={errors.idConsulta}
                                onChange={(value) => {
                                    if (!value) return;

                                    if (value === CARTERA_TOTAL_QUERY_ID) {
                                        setQueryTransversal([]);
                                    }

                                    reset(
                                        { idConsulta: value, nombreConsulta: [] }, // <-- siempre []
                                        { keepErrors: false, keepTouched: false, keepDirty: false }
                                    );
                                    clearErrors();
                                    setQueryParamsState(value);
                                }}
                            />

                            {queryParams && queryParams.map((param, index) => {
                                const name = `nombreConsulta.${index}` as const;
                                const label = param.nombreparametro;
                                const error = errors.nombreConsulta?.[index] as FieldError | undefined;

                                switch (param.tipodato) {
                                    case 'Texto':
                                        if (label === 'RUT') {
                                            return (
                                                <InputRut
                                                    key={name}
                                                    label={label}
                                                    name={name}
                                                    control={control}
                                                    error={error}
                                                />
                                            );
                                        }
                                        return (
                                            <InputText
                                                key={name}
                                                label={label}
                                                name={name}
                                                control={control}
                                                error={error}
                                            />
                                        );
                                    case 'Fecha':
                                        return (
                                            <InputFecha
                                                key={name}
                                                label={label}
                                                name={name}
                                                control={control}
                                                error={error}
                                            />
                                        );
                                    case 'Numero':
                                        return (
                                            <InputNumber
                                                key={name}
                                                label={label}
                                                name={name}
                                                control={control}
                                                error={error}
                                            />
                                        );
                                    default:
                                        return null;
                                }
                            })}

                            <Box sx={{ mt: 2, mb: 2 }}>
                                <PermissionGuard permissions={[TipoFuncionalidad.Ejecutar_query]}>
                                    <Button type="submit" variant="contained" color="primary">
                                        Ejecutar consulta
                                    </Button>
                                </PermissionGuard>

                            </Box>
                        </form>
                    </CustomAccordion>
                    {(queryTransversal && queryTransversal.length > 0) &&
                        <Tabla
                            columns={columnasDinamicas}
                            data={queryTransversal}
                            titulo={
                                <TableHeader
                                    // title="Listado participantes"
                                    buttonExport={
                                        <>
                                            <PermissionGuard permissions={[TipoFuncionalidad.Transversal_exportar]}>
                                                <MButton
                                                    type="button"
                                                    variant="outlined"
                                                    startIcon={<SummarizeTwoTone />}
                                                    onClick={() => exportExcel(queryTransversal, columnasDinamicas, nameArchive)}
                                                >
                                                    Exportar
                                                </MButton>
                                            </PermissionGuard>
                                        </>
                                    }
                                />
                            }
                        />
                    }
                </Paper>
            </Box>
        </Suspense>
    );
}

export default EjecutadorQuery;
